<script lang="ts">
  import type { Period, SLeaderboardEntry } from '../types';
  import { getTileId, scoreTile, scoreWord } from '~/algorithms/letters';
  import { isUUID } from '~/auth/utils';
  import StaticWord from '~/components/StaticWord.svelte';
  import Star from '~/icons/Star.svelte';

  type Props = {
    tab: Period;
    entry: SLeaderboardEntry;
    current?: boolean;
    position?: number;
    variant?: 'replacing' | 'replaced';
  };
  const props: Props = $props();
  const anonymous = $derived(
    isUUID(props.entry.userName) || props.entry.userName === 'anonymous',
  );

  const parseDate = (timestamp: string | { seconds: number }) =>
    typeof timestamp === 'string'
      ? new Date(timestamp)
      : new Date(timestamp.seconds * 1000);

  const formatDate = (date: Date) => {
    const diffMs = Date.now() - date.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays < 7) {
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      if (diffMins < 1) return 'just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${Math.floor(diffDays)}d ago`;
    }
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };
</script>

<div id={props.entry.id} class="container" class:selected={props.current} class:replacing={props.variant === 'replacing'} class:replaced={props.variant === 'replaced'}>
  <div class="row">
    <div class="topline">
      <h2 class="rank">
        {#if props.position !== undefined}
          #{props.position}
        {:else if props.entry.swaps !== undefined}
          <span class="swaps"><Star size="0.85em" />{props.entry.swaps}</span>
        {/if}
      </h2>
      <h3 class="name" class:anonymous>
        {#if props.tab !== 'local'}
          {anonymous ? 'anonymous' : props.entry.userName}
        {/if}
      </h3>
      <h2 class="score">{props.entry.score.toLocaleString()}</h2>
    </div>
  </div>
  <StaticWord
    highlighted="green"
    word={{
      word: props.entry.bestWord.map((tile) => ({
        ...tile,
        points: scoreTile(tile.letter, 'classic'),
        id: getTileId(),
      })),
      score: scoreWord(props.entry.bestWord, 'classic').score,
      swaps: 0,
      axis: 'row',
    }}
  />
  <div class="row">
    <h5 class="date">{formatDate(parseDate(props.entry.date))}</h5>
  </div>
</div>

<style lang="scss">
  .container {
    width: 100%;
    display: grid;
    gap: 0.75em;
    border-bottom: 2px solid grey;
    padding: 0.5em;
  }
  .selected {
    background-color: #80808080;
    border-radius: 0.5em;
    border-bottom: 2px solid darkgrey;
  }
  .replacing {
    border: 2px solid currentColor;
    border-radius: 0.5em;
  }
  .replaced {
    border: 2px dashed currentColor;
    border-radius: 0.5em;
    opacity: 0.5;
  }
  .row {
    width: 100%;
    display: flex;
    align-items: center;
  }
  .topline {
    width: 100%;
    display: grid;
    grid-template-columns: [position] 1fr [name] auto [score] 1fr;
    align-items: center;
  }
  .name {
    grid-column: name;
    &.anonymous {
      opacity: 50%;
    }
  }
  .score {
    grid-column: score;
    align-self: end;
    margin: 0;
    padding: 0;
    text-align: end;
  }
  .rank {
    grid-column: position;
    text-align: start;
    margin: 0;
    padding: 0;
  }
  h3,
  h5 {
    margin: 0;
  }
  .date {
    font-style: italic;
    opacity: 0.6;
    align-self: end;
  }
  .swaps {
    display: flex;
    align-items: center;
    gap: 0.2em;
  }
  h3 {
    display: inline;
  }
</style>
